import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge class names conditionally with Tailwind conflict resolution.
 * Uses clsx for conditional logic + tailwind-merge to resolve conflicts
 * (e.g., `cn('px-4', 'px-6')` → `'px-6'`).
 */
export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs))
}

/**
 * Map a status to a color token class.
 */
export function statusColor(status: 'success' | 'warning' | 'error' | 'info'): string {
	const map = {
		success: 'text-success',
		warning: 'text-warning',
		error: 'text-error',
		info: 'text-info',
	} as const
	return map[status]
}

/**
 * Map a status to a background color class.
 */
export function statusBg(status: 'success' | 'warning' | 'error' | 'info'): string {
	const map = {
		success: 'bg-success/10',
		warning: 'bg-warning/10',
		error: 'bg-error/10',
		info: 'bg-info/10',
	} as const
	return map[status]
}

/**
 * Create data attribute props from an object.
 * Returns a Record suitable for spreading onto JSX elements.
 *
 * Usage:
 *   <div {...dataAttrs({ testId: 'card', variant: 'primary', active: true })} />
 *   // → data-test-id="card" data-variant="primary" data-active
 */
export function dataAttrs(attrs: Record<string, string | number | boolean>): Record<string, string | boolean> {
	const result: Record<string, string | boolean> = {}
	for (const [key, value] of Object.entries(attrs)) {
		const attr = `data-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`
		result[attr] = value === true ? true : String(value)
	}
	return result
}
